import {
  findReference,
  findSpots,
  Suggest,
  type FoundItem,
  type Point,
  type Spot,
} from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MapIcon } from '../place/SpotField';
import { SpotMap } from '../place/SpotMap';

/** How long after the last key the lists are asked. */
const FIND_MS = 250;

/**
 * How those found are offered: each by its name, what is said of it beside; under their maker
 * when grouped, the makers in the order their best is found and each name without its maker's.
 */
function suggestionsOf(items: FoundItem[], grouped: boolean) {
  if (!grouped) {
    return items.map((item) => ({
      value: keyOf(item),
      label: item.name,
      detail: [item.detail, item.country].filter((part) => part !== '').join(' · '),
    }));
  }
  const makers = [...new Set(items.map((item) => item.detail))];
  return items
    .toSorted((a, b) => makers.indexOf(a.detail) - makers.indexOf(b.detail))
    .map((item) => {
      const own = item.name.startsWith(`${item.detail} `)
        ? item.name.slice(item.detail.length + 1)
        : item.name;
      return { value: keyOf(item), label: own, group: item.detail || undefined };
    });
}

/** What a suggestion of one found holds: its code, else its name and country. */
const keyOf = (item: FoundItem) =>
  item.code === '' ? `${item.name}\t${item.country}` : item.code;

/**
 * A text typed with what fits it from the lists of the ground and the sea suggested under it —
 * the ships, the railway companies, the stations — asked of the server once typing pauses; one
 * chosen is handed over whole, anything else typed stays as typed.
 */
export function FoundSuggest({
  label,
  value,
  lists,
  grouped = false,
  onChange,
  onPick,
  near = null,
  picked = null,
  onSpot,
}: {
  label: string;
  value: string;
  /** The lists asked, `rail_operators,transit`; none, OpenStreetMap's places alone. */
  lists?: string | undefined;
  /** Listed under their maker, each by its name without it: `Tesla` over `Model 3`. */
  grouped?: boolean;
  onChange: (value: string) => void;
  onPick: (item: FoundItem) => void;
  /**
   * The town it is in: OpenStreetMap's places near it offered too, a bus station no list has,
   * and the map at the field's end to pin one on; each handed over with where it is.
   */
  near?: Point | null;
  /** Where it is said to be already, pinned when the map opens. */
  picked?: Spot | null;
  onSpot?: ((spot: Spot) => void) | undefined;
}) {
  const { t } = useTranslation();
  // What was found, and for what: an answer to what is no longer typed is not shown.
  const [found, setFound] = useState<{ typed: string; items: FoundItem[] }>({
    typed: '',
    items: [],
  });
  const [spots, setSpots] = useState<{ typed: string; spots: Spot[] }>({
    typed: '',
    spots: [],
  });
  const [mapping, setMapping] = useState(false);
  useEffect(() => {
    if (value.trim() === '' || lists === undefined) return;
    const timer = window.setTimeout(() => {
      findReference(lists, value).then(
        (items) => {
          setFound({ typed: value, items });
        },
        // Not asked: the field is typed as it is.
        () => undefined,
      );
    }, FIND_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [lists, value]);
  const lat = near?.lat;
  const lon = near?.lon;
  const spotted = onSpot !== undefined;
  useEffect(() => {
    if (lat === undefined || lon === undefined || !spotted || value.trim().length < 3) return;
    const stop = new AbortController();
    const timer = window.setTimeout(() => {
      findSpots(value, { lat, lon }, stop.signal).then(
        (answer) => {
          setSpots({ typed: value, spots: answer.spots });
        },
        // None found, or asked again: only the lists' are offered.
        () => undefined,
      );
    }, FIND_MS);
    return () => {
      window.clearTimeout(timer);
      stop.abort();
    };
  }, [lat, lon, spotted, value]);
  const shown = found.typed === value ? found.items : [];
  const places = spots.typed === value ? spots.spots : [];
  return (
    <>
      <Suggest
        label={label}
        value={value}
        suggestions={[
          ...suggestionsOf(shown, grouped),
          ...places.map((spot, at) => ({
            value: `\u0001${String(at)}`,
            label: spot.name,
            detail: [spot.detail, 'OpenStreetMap'].filter((part) => part !== '').join(' · '),
          })),
        ]}
        onChange={(v) => {
          const item = shown.find((one) => keyOf(one) === v);
          const spot = v.startsWith('\u0001') ? places[Number(v.slice(1))] : undefined;
          if (item) onPick(item);
          else if (spot && onSpot) onSpot(spot);
          else onChange(v);
        }}
        {...(near !== null && onSpot
          ? {
              suffix: (
                <button
                  type="button"
                  className="input-suffix input-suffix-button"
                  aria-label={t('spotMap.openFor', { label })}
                  title={t('spotMap.openFor', { label })}
                  onClick={() => {
                    setMapping(true);
                  }}
                >
                  <MapIcon />
                </button>
              ),
            }
          : {})}
      />
      {mapping && near !== null && onSpot && (
        <SpotMap
          near={near}
          // Pinned where it was said to be only when it is still called so; else looked for.
          picked={picked !== null && picked.name === value ? picked : null}
          query={value}
          onClose={() => {
            setMapping(false);
          }}
          onPick={(pinned) => {
            setMapping(false);
            onSpot(pinned);
          }}
        />
      )}
    </>
  );
}
